import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, Phone, Mail } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { useContent } from "../context/ContentContext";
import logo from "../assets/logo.jpeg";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import SearchBar from "./SearchBar";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Design Studio", to: "/customize" },
  { label: "Factory", to: "/factory" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { content, categories } = useContent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden md:block bg-[#14171c] text-neutral-300 text-xs">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#C9A227]" /> {settings.email}</span>
            <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#C9A227]" /> {settings.phone}</span>
          </div>
          <div className="flex items-center gap-2 text-neutral-400">
            <span className="text-[#C9A227] font-medium">Offices:</span>
            <span>{content.officesLine}</span>
          </div>
        </div>
      </div>

      <div className={`transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-md" : "bg-white shadow-sm"}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Best For Medals" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[15px] tracking-wide text-[#14171c]">BEST FOR MEDALS</div>
              <div className="text-[10px] tracking-[0.2em] text-[#C9A227] font-semibold">HK LIMITED</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" className="px-3 py-2 text-sm font-semibold text-neutral-700 hover:text-[#C9A227] transition-colors">Home</Link>
            <div className="relative" onMouseEnter={() => setProdOpen(true)} onMouseLeave={() => setProdOpen(false)}>
              <button className="px-3 py-2 text-sm font-semibold text-neutral-700 hover:text-[#C9A227] transition-colors flex items-center gap-1">
                Products <ChevronDown className="w-4 h-4" />
              </button>
              {prodOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[520px]">
                  <div className="bg-white rounded-xl shadow-2xl border border-neutral-100 p-3 grid grid-cols-2 gap-2 animate-fade-up">
                    {categories.map((c) => (
                      <Link key={c.slug} to={`/category/${c.slug}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-neutral-50 transition-colors group">
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-[#14171c] group-hover:text-[#C9A227] transition-colors">{c.name}</div>
                          <div className="text-xs text-neutral-500">{c.tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {navLinks.slice(1).map((l) => (
              <Link key={l.to} to={l.to} className="px-3 py-2 text-sm font-semibold text-neutral-700 hover:text-[#C9A227] transition-colors">{l.label}</Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden xl:block"><SearchBar variant="desktop" /></div>
            <Button onClick={() => navigate("/contact")} className="hidden sm:inline-flex bg-[#C9A227] hover:bg-[#A6841B] text-white font-semibold rounded-full px-5">
              Request a Quote
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-[#14171c]"><Menu className="w-6 h-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="p-5 border-b flex items-center gap-3">
                  <img src={logo} alt="logo" className="h-10 w-10 object-contain" />
                  <span className="font-display font-extrabold text-sm">BEST FOR MEDALS</span>
                </div>
                <div className="px-4 pt-4">
                  <SearchBar variant="mobile" onNavigate={() => setMobileOpen(false)} />
                </div>
                <div className="p-4 flex flex-col">
                  <SheetClose asChild><Link to="/" className="py-3 border-b text-neutral-800 font-semibold">Home</Link></SheetClose>
                  <div className="py-3 border-b">
                    <div className="text-xs uppercase tracking-widest text-[#C9A227] font-bold mb-2">Products</div>
                    <div className="flex flex-col gap-1">
                      {categories.map((c) => (
                        <SheetClose asChild key={c.slug}>
                          <Link to={`/category/${c.slug}`} className="py-1.5 text-sm text-neutral-700">{c.name}</Link>
                        </SheetClose>
                      ))}
                    </div>
                  </div>
                  {navLinks.slice(1).map((l) => (
                    <SheetClose asChild key={l.to}>
                      <Link to={l.to} className="py-3 border-b text-neutral-800 font-semibold">{l.label}</Link>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Button onClick={() => navigate("/contact")} className="mt-4 bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full">Request a Quote</Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
